// React
import React, { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { deleteOfficeAPI, getOfficesAPI } from "services/api/offices/api";
import { OfficeRow } from "services/api/offices/types";
import { initialDataWithPagination } from "services/api/consts";

// UI
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";

// Lokalne komponente
import OfficeEditCard from "ui/modules/tenants/components/OfficeEditCard/OfficeEditCard";

// Tipovi i konstante
import { TENANT_OFFICE_COLUMNS } from "./consts";
import { TenantOfficeStepProps } from "./types";

const TenantOfficeStep: React.FC<TenantOfficeStepProps> = ({
  formValues,
  onCountChange,
}) => {
  const tenantId = formValues.id;

  const { t } = useTranslate();

  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [officeId, setOfficeId] = useState<string | null>(null);
  const [showOfficeModal, setShowOfficeModal] = useState<boolean>(false);

  const [deleteId, setDeleteId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const fetchOffices = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);

    try {
      const result = await getOfficesAPI(
        initialDataWithPagination.pagination,
        null,
        tenantId
      );

      setOffices(result.data);
      onCountChange?.(result.data.length);
    } catch {
      setOffices([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, onCountChange]);

  useEffect(() => {
    if (tenantId) fetchOffices();
  }, [tenantId, fetchOffices]);

  /*----------------------------------------------------------------
      UNOS I IZMJENA
  ----------------------------------------------------------------*/

  const handleAddOffice = useCallback(() => {
    setOfficeId(null);
    setShowOfficeModal(true);
  }, []);

  const handleRowClick = useCallback((id: string) => {
    setOfficeId(id);
    setShowOfficeModal(true);
  }, []);

  const handleOfficeSuccess = useCallback(() => {
    setShowOfficeModal(false);
    setOfficeId(null);
    fetchOffices();
  }, [fetchOffices]);

  const handleOfficeClose = useCallback(() => {
    setShowOfficeModal(false);
    setOfficeId(null);
  }, []);

  /*----------------------------------------------------------------
      BRISANJE
  ----------------------------------------------------------------*/

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteId) return;

    try {
      const deleted = await deleteOfficeAPI(deleteId, t);

      if (deleted) await fetchOffices();
    } finally {
      setOpenDialog(false);
      setDeleteId("");
    }
  }, [deleteId, t, fetchOffices]);

  const handleDeleteCancel = useCallback(() => {
    setOpenDialog(false);
    setDeleteId("");
  }, []);

  return (
    <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Poslovnice</h1>

        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto">
          Dodajte poslovnice koje pripadaju ovom tenantu. Klikom na redak otvara se izmjena.
        </p>

        {offices.length > 0 && (
          <div className="flex justify-end mb-4">
            <Button
              variant="primary"
              children="Dodaj poslovnicu"
              startIcon="/media/icons/plus.svg"
              onClick={handleAddOffice}
            />
          </div>
        )}

        {/*
          Dok nema nijedne poslovnice tablica ne bi imala što pokazati osim praznih zaglavlja,
          pa na tom mjestu stoji samo poziv na unos.
        */}
        {offices.length === 0 && !isLoading ? (
          <div className="max-w-md mx-auto">
            <Button
              variant="primary"
              size="full"
              children="Dodaj poslovnicu"
              startIcon="/media/icons/plus.svg"
              onClick={handleAddOffice}
            />
          </div>
        ) : (
          <TableSearch
            columns={TENANT_OFFICE_COLUMNS(handleDeleteClick)}
            data={offices}
            selectableRows={false}
            onRowClick={handleRowClick}
            isLoading={isLoading}
            disablePagination
          />
        )}
      </div>

      {/*
        Poslovnica je ovdje jedan korak unosa tenanta, a ne zaseban početak: zato manji
        modal na sredini, bez uvodne plohe koja bi bila drugi uvod usred prvog.
      */}
      <OfficeEditCard
        officeId={officeId}
        show={showOfficeModal}
        isFullScreen={false}
        onSuccess={handleOfficeSuccess}
        onClose={handleOfficeClose}
        tenantId={tenantId ?? undefined}
      />

      <Dialog
        open={openDialog}
        title="Brisanje poslovnice"
        message="Poslovnica se miče s popisa zajedno sa svojim adresama, kontaktima i bilješkama. Kroz sučelje se ne može vratiti."
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default memo(TenantOfficeStep);
